import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { mediaLibraryService } from '@/entities/MediaLibrary/api/mediaLibraryService';
import { formatMediaFileCount, formatMediaSize } from '@/entities/MediaLibrary/model/format';
import { getErrorMessage } from '@/shared/lib/errors';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Modal } from '@/shared/ui/Modal/Modal';
import classes from './MediaLibrary.module.scss';

interface UploadItem {
    id: string;
    file: File;
    status: 'queued' | 'uploading' | 'success' | 'error';
    progress: number;
    error?: string;
}

interface MediaUploadDialogProps {
    folderId?: string;
    folderPath: string;
    onClose: () => void;
    onChanged: () => void;
}

/** Показывает миниатюру локального файла до отправки */
const UploadPreview = ({ file }: { file: File }) => {
    const [url, setUrl] = useState<string>();

    useEffect(() => {
        const preview = URL.createObjectURL(file);
        setUrl(preview);

        return () => URL.revokeObjectURL(preview);
    }, [file]);

    return <img
        className={classes.uploadThumbnail}
        src={url}
        alt={file.name}
        loading="lazy"
        decoding="async"
    />;
};

/** Загружает очередь изображений с индивидуальным прогрессом и повтором */
export const MediaUploadDialog = (
    { folderId, folderPath, onClose, onChanged }: MediaUploadDialogProps
) => {
    const [items, setItems] = useState<UploadItem[]>([]);
    const [busy, setBusy] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [error, setError] = useState('');
    const [started, setStarted] = useState(false);
    const input = useRef<HTMLInputElement>(null);
    const uploading = useRef(false);
    const summary = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (started) summary.current?.focus();
    }, [started]);

    /** Добавляет допустимые файлы в очередь */
    const add = (files: FileList | null): void => {
        if (!files || started || uploading.current) return;
        const accepted = Array.from(files).filter(file => ['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type) && file.size > 0 && file.size <= 25 * 1024 * 1024);

        const invalid = accepted.length !== files.length
            ? 'Часть файлов отклонена: допустимы PNG, JPEG, WEBP, GIF до 25 МБ.'
            : '';

        const overflow = items.length + accepted.length > 50
            ? 'В очереди не более 50 файлов. Остальные выберите отдельно.'
            : '';

        setError([invalid, overflow].filter(Boolean).join(' '));

        setItems(previous => [...previous, ...accepted.map(file => ({
            id: crypto.randomUUID(),
            file,
            status: 'queued' as const,
            progress: 0
        }))].slice(0, 50));
    };

    /** Принимает файлы из системного диалога */
    const change = (event: ChangeEvent<HTMLInputElement>): void => {
        add(event.target.files);
        event.target.value = '';
    };

    /** Принимает перетаскиваемые изображения */
    const drop = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        setDragging(false);
        add(event.dataTransfer.files);
    };

    /** Обновляет один элемент очереди */
    const update = (id: string, changes: Partial<UploadItem>): void => setItems(previous => previous.map(item => item.id === id
        ? {
            ...item,
            ...changes
        }
        : item));

    /** Передаёт ожидающие файлы и повторяет неудачные загрузки */
    const upload = async (onlyId?: string): Promise<void> => {
        const queue = items.filter(entry => (!onlyId || entry.id === onlyId) && (entry.status === 'queued' || entry.status === 'error'));

        if (uploading.current || !queue.length) return;
        uploading.current = true;
        summary.current?.focus();
        setBusy(true);
        setStarted(true);
        setError('');

        for (const item of queue) {
            update(item.id, {
                status: 'uploading',
                progress: 0,
                error: undefined
            });

            try {
                await mediaLibraryService.upload(item.file, folderId, progress => update(item.id, { progress }));

                update(item.id, {
                    status: 'success',
                    progress: 100
                });
            } catch (failure: unknown) {
                update(item.id, {
                    status: 'error',
                    error: getErrorMessage(failure, 'Не удалось загрузить')
                });

                continue;
            }

            onChanged();
        }

        uploading.current = false;
        setBusy(false);
    };

    const pending = items.some(item => item.status === 'queued' || item.status === 'error');
    const succeeded = items.filter(item => item.status === 'success').length;

    const progress = items.length
        ? Math.floor(items.reduce((sum, item) => sum + (item.status === 'success'
            ? 100
            : item.status === 'uploading'
                ? Math.min(99, Math.max(0, item.progress))
                : 0), 0) / items.length)
        : 0;

    return <Modal
        appearance="surface"
        visible
        title={started
            ? 'Загрузка файлов'
            : 'Загрузить файлы'
        }
        onHide={onClose}
        closable={!busy}
        size="md"
        className={classes.uploadDialog}
    >
        <div className={classes.dialogForm}>
            <p className={classes.uploadDestination}>Папка назначения: <i
                className="pi pi-folder"
                aria-hidden="true"
            /> <span>
                    {folderPath}
                </span>
            </p>
            {started &&
                <div
                    ref={summary}
                    className={classes.uploadSummary}
                    role="region"
                    aria-label="Ход загрузки"
                    tabIndex={-1}
                >
                    <div aria-live="polite">
                        <span>
                            {succeeded} из {items.length} файлов</span>
                        <span>
                            {progress}%</span>
                    </div>
                    <progress
                        aria-label="Общий прогресс загрузки"
                        max={100}
                        value={progress}
                    />
                </div>
            }
            {!started &&
                <div
                    className={`${classes.dropzone} ${dragging
                        ? classes.dragging
                        : ''}`}
                    onDragOver={event => {
                        event.preventDefault();
                        setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={drop}
                >
                    <i
                        aria-hidden="true"
                        className="pi pi-cloud-upload"
                    />
                    <h3>Перетащите файлы сюда</h3>
                    <p>или выберите на компьютере</p>
                    <ActionButton
                        type="button"
                        outlined
                        color="secondary"
                        onClick={() => input.current?.click()}
                        disabled={busy}
                    >
                        Выбрать файлы
                    </ActionButton>
                    <small>PNG, JPEG, WEBP, GIF · до 25 МБ на файл</small>
                    <input
                        ref={input}
                        type="file"
                        multiple
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={change}
                        aria-label="Выбрать изображения"
                        hidden
                    />
                </div>
            }
            {error &&
                <p
                    role="alert"
                    className={classes.error}
                >
                    {error}
                </p>
            }
            {items.length > 0 &&
                <section
                    className={classes.uploadFiles}
                    aria-label="Выбранные файлы"
                >
                    {!started &&
                        <h4>Выбранные файлы ({items.length})</h4>
                    }
                    <ul className={`${classes.uploadQueue} ${started
                        ? classes.uploadInProgress
                        : ''}`}>
                        {items.map(item => <li
                            key={item.id}
                            className={classes.uploadRow}
                        >
                            <UploadPreview file={item.file} />
                            <div className={classes.uploadFileName}>
                                <strong title={item.file.name}>
                                    {item.file.name}
                                </strong>
                                <small>
                                    {formatMediaSize(item.file.size)}
                                </small>
                            </div>
                            {started &&
                                <div
                                    className={classes.uploadStatus}
                                    aria-live="polite"
                                >
                                    {item.status === 'success' &&
                                        <span className={classes.uploadSuccess}>
                                            <i
                                                className="pi pi-check-circle"
                                                aria-hidden="true"
                                            /> Загружен</span>
                                    }
                                    {item.status === 'uploading' &&
                                        <>
                                            <progress
                                                aria-label={`Прогресс ${item.file.name}`}
                                                max={100}
                                                value={item.progress}
                                            />
                                            <span>
                                                {item.progress === 100
                                                    ? 'Обработка…'
                                                    : `${item.progress}%`}
                                            </span>
                                        </>
                                    }
                                    {item.status === 'queued' &&
                                        <span>Ожидает загрузки</span>
                                    }
                                    {item.status === 'error' &&
                                        <>
                                            <span
                                                className={classes.error}
                                                title={item.error}
                                            >
                                                <i
                                                    className="pi pi-exclamation-circle"
                                                    aria-hidden="true"
                                                /> Не удалось загрузить
                                            </span>
                                            <ActionButton
                                                type="button"
                                                text
                                                color="secondary"
                                                aria-label={`Повторить ${item.file.name}`}
                                                disabled={busy}
                                                onClick={() => void upload(item.id)}
                                            >
                                                Повторить
                                            </ActionButton>
                                            <small className={classes.uploadFailure}>
                                                {item.error}
                                            </small>
                                        </>
                                    }
                                </div>
                            }
                            {!started &&
                                <ActionButton
                                    type="button"
                                    icon="pi pi-times"
                                    iconOnly
                                    outlined
                                    color="secondary"
                                    aria-label={`Убрать ${item.file.name}`}
                                    onClick={() => setItems(previous => previous.filter(entry => entry.id !== item.id))}
                                />
                            }
                        </li>)}
                    </ul>
                </section>
            }
            {started &&
                <p className={classes.uploadHint}>
                    <i
                        className="pi pi-info-circle"
                        aria-hidden="true"
                    /> Успешно загруженные файлы уже доступны в медиатеке</p>
            }
            <div className={classes.uploadFooter}>
                {!started &&
                    <>
                        <ActionButton
                            type="button"
                            outlined
                            color="secondary"
                            onClick={onClose}
                            disabled={busy}
                        >
                            Отмена
                        </ActionButton>
                        <ActionButton
                            type="button"
                            loading={busy}
                            disabled={!pending}
                            onClick={() => void upload()}
                        >
                            Загрузить {formatMediaFileCount(items.length)}
                        </ActionButton>
                    </>
                }
                {started &&
                    <ActionButton
                        type="button"
                        disabled={busy || pending}
                        onClick={onClose}
                    >
                        Готово
                    </ActionButton>
                }
            </div>
        </div>
    </Modal>;
};
